import { Helmet } from "react-helmet-async";
import {
  Container,
  Typography,
  Divider,
  Stack,
  Button,
  Box,
  Paper,
  alpha,
  useTheme,
} from "@mui/material";
import useResponsive from "../hooks/useResponsive";
import Iconify from "../components/iconify";
import { LoginForm } from "../sections/auth/login";
import logoImg from "../img/LOGO-Icontec-2019_hor_pos.png";

// ----------------------------------------------------------------------

const IMAGES = {
  logo: logoImg,
  sideImg: "https://cdn-icons-png.flaticon.com/512/4832/4832193.png",
};

// ----------------------------------------------------------------------

const CustomButton = ({
  onClick,
  text,
  icon,
  background = "#0085CA",
  hoverBackground = "#006FA8",
  outlined = false,
}) => (
  <Button
    onClick={onClick}
    fullWidth
    size="large"
    variant={outlined ? "outlined" : "contained"}
    startIcon={icon}
    sx={{
      minHeight: 54,
      borderRadius: "12px",
      textTransform: "none",
      fontSize: "0.95rem",
      fontWeight: 600,
      letterSpacing: "0.1px",
      ...(outlined
        ? {
            color: "#0085CA",
            borderColor: alpha("#0085CA", 0.35),
            backgroundColor: "#fff",
            "&:hover": {
              borderColor: "#0085CA",
              backgroundColor: alpha("#0085CA", 0.04),
              transform: "translateY(-1px)",
            },
          }
        : {
            color: "#fff",
            background: background,
            boxShadow: `0 8px 20px ${alpha(background, 0.22)}`,
            "&:hover": {
              background: hoverBackground,
              boxShadow: `0 10px 25px ${alpha(background, 0.3)}`,
              transform: "translateY(-2px)",
            },
          }),
      transition: "all 0.25s ease",
    }}
  >
    {text}
  </Button>
);

// ----------------------------------------------------------------------

export default function LoginPage() {
  const mdUp = useResponsive("up", "md");
  const theme = useTheme();

  const postData = () => {
    window.location.href =
      "https://middlewarebackprd.azurewebsites.net/api/v1/AuthAzure";
  };

  const consulta = () => {
    window.location.href = "/";
  };

  return (
    <>
      <Helmet>
        <title>Inicio de sesión | Huella de Confianza</title>
        <meta
          name="description"
          content="Acceso a la plataforma Huella de Confianza"
        />
      </Helmet>

      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          background: "#F5F8FA",
        }}
      >
        {/* ============================================================= */}
        {/* PANEL IZQUIERDO */}
        {/* ============================================================= */}

        {mdUp && (
          <Box
            sx={{
              width: { md: "42%", lg: "45%" },
              minHeight: "100vh",
              position: "relative",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(145deg, #005B8F 0%, #0085CA 55%, #00A6D6 100%)",
            }}
          >
            {/* Decoraciones */}

            <Box
              sx={{
                position: "absolute",
                width: 420,
                height: 420,
                borderRadius: "50%",
                background: alpha("#fff", 0.06),
                top: -180,
                right: -160,
              }}
            />

            <Box
              sx={{
                position: "absolute",
                width: 320,
                height: 320,
                borderRadius: "50%",
                border: `1px solid ${alpha("#fff", 0.12)}`,
                bottom: -140,
                left: -120,
              }}
            />

            {/* Contenido */}

            <Box
              sx={{
                position: "relative",
                zIndex: 2,
                width: "100%",
                maxWidth: 520,
                px: 7,
                color: "#fff",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  opacity: 0.8,
                  mb: 2,
                }}
              >
                Huella de Confianza
              </Typography>

              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.1,
                  fontSize: {
                    md: "2.7rem",
                    lg: "3.3rem",
                  },
                  mb: 2.5,
                }}
              >
                La confianza también deja huella.
              </Typography>

              <Typography
                sx={{
                  fontSize: "1.05rem",
                  lineHeight: 1.7,
                  color: alpha("#fff", 0.82),
                  maxWidth: 440,
                  mb: 5,
                }}
              >
                Accede de forma segura a nuestra plataforma para gestionar,
                consultar y verificar la información relacionada con los
                procesos de certificación.
              </Typography>

              {/* Imagen */}

              <Box
                sx={{
                  position: "relative",
                  borderRadius: "20px",
                  overflow: "hidden",
                  boxShadow: "0 25px 60px rgba(0,0,0,0.22)",
                  border: `1px solid ${alpha("#fff", 0.18)}`,
                }}
              >
                <Box
                  component="img"
                  src={IMAGES.sideImg}
                  alt="Huella de Confianza"
                  sx={{
                    display: "block",
                    width: "100%",
                    height: 350,
                    objectFit: "cover",
                    filter: "brightness(0.78)",
                  }}
                />

                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(180deg, transparent 20%, rgba(0,50,80,0.55) 100%)",
                  }}
                />
              </Box>

              <Typography
                sx={{
                  mt: 3,
                  fontSize: "0.8rem",
                  color: alpha("#fff", 0.6),
                }}
              >
                Plataforma institucional • Acceso seguro
              </Typography>
            </Box>
          </Box>
        )}

        {/* ============================================================= */}
        {/* PANEL DERECHO */}
        {/* ============================================================= */}

        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: { xs: 2, sm: 4 },
            py: { xs: 4, md: 6 },
          }}
        >
          <Container
            maxWidth="sm"
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 480,
                p: { xs: 3, sm: 5 },
                borderRadius: "24px",
                backgroundColor: "#fff",
                border: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
                boxShadow: "0 20px 60px rgba(0, 70, 100, 0.08)",
              }}
            >
              {/* Logo */}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: 4,
                }}
              >
                <Box
                  component="img"
                  src={IMAGES.logo}
                  alt="ICONTEC"
                  sx={{
                    width: { xs: 170, sm: 200 },
                    height: "auto",
                    objectFit: "contain",
                  }}
                />
              </Box>

              {/* Encabezado */}

              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "#1C2733",
                    mb: 1,
                  }}
                >
                  Bienvenido
                </Typography>

                <Typography
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.95rem",
                  }}
                >
                  Ingresa a Huella de Confianza
                </Typography>
              </Box>

              {/* Microsoft */}

              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    color: "text.secondary",
                    mb: 1.2,
                  }}
                >
                  ACCESO INSTITUCIONAL
                </Typography>

                <CustomButton
                  onClick={postData}
                  text="Ingresar con Microsoft"
                  icon={<Iconify icon="mdi:microsoft" width={23} height={23} />}
                  background="#0078D4"
                  hoverBackground="#0067B8"
                />
              </Box>

              <Divider sx={{ my: 3 }}>
                <Typography
                  sx={{
                    px: 1,
                    color: "text.disabled",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                  }}
                >
                  O INGRESA CON TUS CREDENCIALES
                </Typography>
              </Divider>

              {/* Login */}

              <Box>
                <LoginForm />
              </Box>

              {/* Consulta pública */}

              <Box
                sx={{
                  mt: 4,
                  pt: 3,
                  borderTop: `1px solid ${alpha(
                    theme.palette.grey[500],
                    0.12,
                  )}`,
                }}
              >
                <Typography
                  sx={{
                    textAlign: "center",
                    fontSize: "0.8rem",
                    color: "text.secondary",
                    mb: 1.5,
                  }}
                >
                  ¿Quieres consultar información pública?
                </Typography>

                <CustomButton
                  onClick={consulta}
                  text="Ir a Consulta Pública"
                  icon={<Iconify icon="mdi:magnify" width={22} height={22} />}
                  outlined
                />
              </Box>

              {/* Footer */}

              <Typography
                sx={{
                  textAlign: "center",
                  mt: 4,
                  fontSize: "0.72rem",
                  color: "text.disabled",
                  lineHeight: 1.5,
                }}
              >
                Acceso protegido y seguro
                <br />
                Huella de Confianza © {new Date().getFullYear()}
              </Typography>
            </Paper>
          </Container>
        </Box>
      </Box>
    </>
  );
}
